import { useState, useEffect, useRef, memo } from 'react';

import { useManualQuery } from '@zenbusiness/application-commons-graphql';
import delay from '@zenbusiness/application-commons-utils/delay';
import isNotUndefinedOrNull from '@zenbusiness/application-commons-utils/isNotUndefinedOrNull';
import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import { produce } from 'immer';
import PropTypes from 'prop-types';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';

import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import LoadingSpinner from '#client/components/LoadingSpinner';
import useFormStep from '#client/hooks/useFormStep';
import { GET_INSTANT_FUNDING_TRANSFER_ROUTINE } from '#client/queries';

import { SpinnerContainer, StepItemStyle } from './Common.styles';
import { DialogContentStyled } from './DialogAndWrapper.styles';
import { SummaryFormWrapperStyled } from './SummaryFormWrapper.styles';

import { INSTANT_TRANSFER_STATUS, MODAL_ACTION } from '../constants/instants';

const MAX_API_CALLS = 3;
/**
 * Initiating: When the component loaded, then the first step is active after 2s
 * Processing: When calling the api query, and the transfer status is pending
 * Finalizing: When transfer status is processed or reaching the max api calling
 */
const STEPS = [
  {
    name: 'Initiating',
    order: 1,
    isVisible: true,
    isActive: false
  },
  {
    name: 'Processing',
    order: 2,
    isVisible: false,
    isActive: false
  },
  {
    name: 'Finalizing',
    order: 3,
    isVisible: false,
    isActive: false
  }
];

const Step = ({ name, isVisible, isActive }) => (
  <Stack>
    {isVisible && (
      <StepItemStyle>
        <Stack
          alignItems="center"
          direction="row"
        >
          <Box mr="8px">
            {isActive ? (
              <CheckCircleRoundedIcon
                fontSize="small"
                sx={{
                  color: '#33F28B'
                }}
              />
            ) : (
              <CircleOutlinedIcon fontSize="small" />
            )}
          </Box>
          <Text size="md">{name}</Text>
        </Stack>
      </StepItemStyle>
    )}
  </Stack>
);

Step.propTypes = {
  name: PropTypes.string.isRequired,
  isVisible: PropTypes.bool.isRequired,
  isActive: PropTypes.bool.isRequired
};

const STATUS = {
  INITIATING: 'initiating',
  ACTIVE: 'active',
  COMPLETED: 'completed'
};

const updateSteps = (status, steps) =>
  produce(steps, (draft) => {
    const draftCopy = draft;

    switch (status) {
      case STATUS.INITIATING:
        draftCopy[0].isActive = true;
        draftCopy[1].isVisible = true;
        return;
      case STATUS.ACTIVE:
        if (draftCopy[0].isActive && draftCopy[2].isVisible) {
          return;
        }
        draftCopy[1].isActive = true;
        draftCopy[2].isVisible = true;
        return;
      case STATUS.COMPLETED:
        draftCopy[1].isActive = true;
        draftCopy[2].isVisible = true;
        draftCopy[2].isActive = true;
        return;
      default:
        draftCopy[0].isActive = false;
        draftCopy[1].isVisible = false;
        draftCopy[2].isVisible = false;
    }
  });

const LoaderSteps = ({ status }) => {
  const [steps, setSteps] = useState(STEPS);

  useEffect(() => {
    setSteps((prevSteps) => updateSteps(status, prevSteps));
  }, [status]);

  return (
    <>
      {steps.map(({ name, isVisible, isActive }) => (
        <Step
          isActive={isActive}
          isVisible={isVisible}
          key={name}
          name={name}
        />
      ))}
    </>
  );
};
LoaderSteps.propTypes = {
  status: PropTypes.string.isRequired
};

const Loader = () => {
  const [count, setCount] = useState(0);
  const [status, setStatus] = useState('');
  const [transferRoutineData, setTransferRoutineData] = useState(null);
  const intervalIdRef = useRef(null);
  const { depositData, dispatchDepositData } = useFormStep();
  const [getInstantTransferRoutine] = useManualQuery(GET_INSTANT_FUNDING_TRANSFER_ROUTINE, {
    noThrow: true
  });
  const transferStatus = isNotUndefinedOrNull(transferRoutineData)
    ? transferRoutineData?.instantFundingTransferRoutine?.transfers?.[0]?.transferStatus
    : '';
  const transferReason = isNotUndefinedOrNull(transferRoutineData)
    ? transferRoutineData?.instantFundingTransferRoutine?.transfers?.[0]?.transferFailureReason
    : '';

  const setActiveFirstStep = () => {
    setStatus(STATUS.INITIATING);
  };

  useEffect(() => {
    delay(setActiveFirstStep, 2000);
  }, []);

  useEffect(() => {
    intervalIdRef.current = setInterval(async () => {
      if (depositData?.instantTransferRoutineUuid && count < MAX_API_CALLS) {
        const { data } = await getInstantTransferRoutine({
          variables: {
            transferRoutineUuid: depositData.instantTransferRoutineUuid
          },
          skipCache: true
        });
        setTransferRoutineData(data);
      }

      setCount((prevCount) => prevCount + 1);
    }, 5000);
    // Cleanup function to clear the interval
    return () => {
      clearInterval(intervalIdRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means the effect runs only once after the initial render

  useEffect(() => {
    if (count === MAX_API_CALLS || transferStatus === INSTANT_TRANSFER_STATUS.PROCESSED) {
      clearInterval(intervalIdRef.current);
    }
  }, [count, transferStatus]);

  useEffect(() => {
    // Based on the status API to set the next step
    if (count > 1 && count <= MAX_API_CALLS - 1) {
      setStatus(STATUS.ACTIVE);
    }

    // when the count reaches MAX_API_CALLS or the status is completed
    if (count === MAX_API_CALLS || transferStatus === INSTANT_TRANSFER_STATUS.PROCESSED) {
      setStatus(STATUS.COMPLETED);
    }
  }, [count, transferStatus]);

  useEffect(() => {
    if (status === STATUS.COMPLETED) {
      // Go to the confirmation screen
      // We need wait a little time to show the completed status on loader screen
      setTimeout(() => {
        dispatchDepositData({
          type: MODAL_ACTION.GO_TO_CONFIRMATION,
          payload: {
            isDisableBack: true,
            // If we do not have transfer status from api, it means the transfer is processing
            transferStatus: transferStatus || INSTANT_TRANSFER_STATUS.PENDING,
            transferReason
          }
        });
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, transferReason, transferStatus]);

  return (
    <DialogContentStyled>
      <ContainerGrid>
        <FocusGrid>
          <SummaryFormWrapperStyled>
            <Stack
              alignItems="center"
              mt="80px"
            >
              <Stack>
                <SpinnerContainer>
                  <LoadingSpinner />
                </SpinnerContainer>
                <Stack
                  mt={{ xs: '16px', md: '24px' }}
                  spacing="4px"
                >
                  <Heading
                    align="center"
                    family="secondary"
                    size="2xl"
                    weight="bold"
                  >
                    Your deposit is processing
                  </Heading>
                  <Text
                    align="center"
                    color="tertiary"
                    size="lg"
                  >
                    It takes about 15 seconds. Hang tight!
                  </Text>
                </Stack>
              </Stack>
              <Stack
                gap="12px"
                mt="16px"
              >
                <LoaderSteps status={status} />
              </Stack>
            </Stack>
          </SummaryFormWrapperStyled>
        </FocusGrid>
      </ContainerGrid>
    </DialogContentStyled>
  );
};

export default memo(Loader);
